import { supabase } from './supabase';
import { extractFnError } from './odooInvoices';

// Accès front aux données SDD Qonto (super admin) : statut connexion + mandats + collectes,
// et démarrage du flux OAuth. Résilient : ne jette jamais.

export interface QontoConnection {
  connected: boolean;
  env?: string | null;
  organization_id?: string | null;
  bank_account_id?: string | null;
  iban?: string | null;
  connected_at?: string | null;
}

export interface QontoTenantMandate {
  slug: string;
  name: string;
  mandate_status?: string | null;
  mandate_umr?: string | null;
  sign_url?: string | null;
  has_client?: boolean;
}

export interface QontoCollectionRow {
  id: string;
  tenant_id?: string | null;
  reference?: string | null;
  amount_cents?: number | null;
  currency?: string | null;
  collection_date?: string | null;
  status?: string | null;
  status_reason?: string | null;
  created_at?: string | null;
}

export interface QontoOverview {
  ok: boolean;
  connection: QontoConnection | null;
  tenants: QontoTenantMandate[];
  collections: QontoCollectionRow[];
  error?: string;
}

/** Vue d'ensemble SDD (RPC `qonto_sdd_overview`, super_admin). */
export async function loadQontoOverview(): Promise<QontoOverview> {
  const { data, error } = await supabase.rpc('qonto_sdd_overview');
  if (error) return { ok: false, connection: null, tenants: [], collections: [], error: error.message };
  const raw = (data ?? {}) as {
    connection?: QontoConnection;
    tenants?: QontoTenantMandate[];
    recent_collections?: QontoCollectionRow[];
  };
  return {
    ok: true,
    connection: raw.connection ?? null,
    tenants: Array.isArray(raw.tenants) ? raw.tenants : [],
    collections: Array.isArray(raw.recent_collections) ? raw.recent_collections : [],
  };
}

/** Démarre l'OAuth Qonto (Edge Function `qonto-oauth-start`) → renvoie l'URL d'autorisation. */
export async function startQontoConnect(): Promise<{ ok: boolean; url?: string; error?: string }> {
  const { data, error } = await supabase.functions.invoke('qonto-oauth-start', { body: {} });
  if (error) return { ok: false, error: await extractFnError(error, 'Connexion impossible.') };
  const res = (data ?? {}) as { ok?: boolean; url?: string; error?: string };
  if (!res.ok || !res.url) return { ok: false, error: res.error ?? 'Échec.' };
  return { ok: true, url: res.url };
}

/** Synchronise les statuts mandat depuis Qonto (Edge Function `qonto-sync`). */
export async function syncQontoStatuses(): Promise<{ ok: boolean; updated?: number; error?: string }> {
  const { data, error } = await supabase.functions.invoke('qonto-sync', { body: {} });
  if (error) return { ok: false, error: await extractFnError(error, 'Synchronisation impossible.') };
  const res = (data ?? {}) as { ok?: boolean; updated?: number; error?: string };
  if (!res.ok) return { ok: false, error: res.error ?? 'Échec.' };
  return { ok: true, updated: res.updated ?? 0 };
}

/** Déclenche un encaissement SEPA (Edge Function `qonto-collect`). */
export async function triggerCollect(slug: string, amountCents: number, reference?: string): Promise<{ ok: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke('qonto-collect', {
    body: { tenant_slug: slug, amount_cents: amountCents, reference },
  });
  if (error) return { ok: false, error: await extractFnError(error, 'Prélèvement impossible.') };
  const res = (data ?? {}) as { ok?: boolean; error?: string };
  if (!res.ok) return { ok: false, error: res.error ?? 'Échec.' };
  return { ok: true };
}

export interface ReconRow {
  slug: string; name: string; ref: string;
  invoiced_cents: number; invoice_state: string | null; payment_state: string | null;
  collected_cents: number; collection_status: string | null;
  recon: 'reconciled' | 'ecart' | 'not_collected' | 'failed' | 'pending' | 'no_invoice' | string;
}

/** Rapprochement Qonto↔Odoo du mois (Edge Function `qonto-recon`). */
export async function loadRecon(tag?: string): Promise<{ ok: boolean; tag?: string; rows: ReconRow[]; error?: string }> {
  const { data, error } = await supabase.functions.invoke('qonto-reconciliation', { body: tag ? { tag } : {} });
  if (error) return { ok: false, rows: [], error: await extractFnError(error, 'Rapprochement impossible.') };
  const res = (data ?? {}) as { ok?: boolean; tag?: string; rows?: ReconRow[]; error?: string };
  if (!res.ok) return { ok: false, rows: [], error: res.error ?? 'Échec.' };
  return { ok: true, tag: res.tag, rows: Array.isArray(res.rows) ? res.rows : [] };
}

/** Marque la facture Odoo d'un resto payée (Edge Function `qonto-recon`, action mark_paid). */
export async function markInvoicePaid(slug: string, tag: string): Promise<{ ok: boolean; message?: string; error?: string }> {
  const { data, error } = await supabase.functions.invoke('qonto-reconciliation', { body: { action: 'mark_paid', tenant_slug: slug, tag } });
  if (error) return { ok: false, error: await extractFnError(error, 'Marquage impossible.') };
  const res = (data ?? {}) as { ok?: boolean; message?: string; error?: string };
  if (!res.ok) return { ok: false, error: res.error ?? 'Échec.' };
  return { ok: true, message: res.message };
}

/** Lance le prélèvement mensuel de tous les restos (Edge Function `qonto-collect-monthly`). */
export async function runMonthlyCollect(): Promise<{ ok: boolean; tag?: string; collected?: number; failed?: number; error?: string }> {
  const { data, error } = await supabase.functions.invoke('qonto-collect-monthly', { body: { all: true } });
  if (error) return { ok: false, error: await extractFnError(error, 'Prélèvement mensuel impossible.') };
  const res = (data ?? {}) as { ok?: boolean; tag?: string; results?: { ok?: boolean }[]; error?: string };
  if (!res.ok) return { ok: false, error: res.error ?? 'Échec.' };
  const list = Array.isArray(res.results) ? res.results : [];
  return { ok: true, tag: res.tag, collected: list.filter((r) => r.ok).length, failed: list.filter((r) => r.ok === false).length };
}

export interface CreateMandateResult { ok: boolean; signUrl?: string | null; status?: string; error?: string }

/** Crée le mandat SEPA d'un resto (Edge Function `qonto-mandate`) → email de signature + sign_url. */
export async function createMandate(slug: string, firstAmountCents?: number): Promise<CreateMandateResult> {
  const { data, error } = await supabase.functions.invoke('qonto-mandate', {
    body: { tenant_slug: slug, first_amount_cents: firstAmountCents },
  });
  if (error) return { ok: false, error: await extractFnError(error, 'Création du mandat impossible.') };
  const res = (data ?? {}) as { ok?: boolean; sign_url?: string | null; status?: string; error?: string };
  if (!res.ok) return { ok: false, error: res.error ?? 'Échec.' };
  return { ok: true, signUrl: res.sign_url ?? null, status: res.status };
}
